import { useEffect } from 'react';

import type { KeyValueStore } from '@runtime/storage.js';

import { fetchRevocationList } from './boot.js';

/**
 * Default cadence for the mid-session revocation re-check. The launcher fetches
 * the revocation list once at boot; kiosks run for weeks without rebooting, so
 * without this a revocation wouldn't take effect until the next restart. One
 * hour trades near-real-time propagation for negligible request volume (a rare,
 * admin-initiated event) — much tighter than "next reboot".
 */
export const REVOCATION_REFRESH_INTERVAL_MS = 60 * 60_000;

export interface RevocationWatch {
  /** The verified license's id — only set when the running license has one. */
  licenseId: string;
  /** Signed revocation-list endpoint for this tenant. */
  url: string;
  tenantId: string;
}

export interface UseRevocationRefreshOptions {
  /** What to watch; null disables the poll (no license id / no API base). */
  watch: RevocationWatch | null;
  store: KeyValueStore;
  /** Called once when the running license's id appears in a fresh list. */
  onRevoked: () => void;
  intervalMs?: number;
  /** Test seam — defaults to the real {@link fetchRevocationList}. */
  fetchList?: typeof fetchRevocationList;
}

/**
 * Periodically re-fetch the signed revocation list mid-session and, if the
 * license the device is CURRENTLY running on has been revoked, invoke
 * `onRevoked` (the caller re-boots, which re-verifies and drops to Core).
 *
 * Reuses {@link fetchRevocationList}, so each poll also refreshes the cached
 * list a subsequent offline boot would fall back to — and inherits its
 * fail-open behavior (a fetch/verify failure leaves the running license alone).
 * Only a device whose OWN license id is revoked reacts; a tenant's other
 * revocations are ignored here.
 */
export function useRevocationRefresh({
  watch,
  store,
  onRevoked,
  intervalMs = REVOCATION_REFRESH_INTERVAL_MS,
  fetchList = fetchRevocationList,
}: UseRevocationRefreshOptions): void {
  useEffect(() => {
    if (watch === null) return undefined;
    let cancelled = false;
    let fired = false;

    async function check(): Promise<void> {
      if (cancelled || fired || watch === null) return;
      const revoked = await fetchList(watch.url, watch.tenantId, store);
      if (cancelled || fired) return;
      if (revoked.has(watch.licenseId)) {
        fired = true;
        onRevoked();
      }
    }

    const handle = setInterval(() => void check(), intervalMs);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [watch, store, onRevoked, intervalMs, fetchList]);
}
